import { ActionIcon, Box, Grid, Group, Loader, Radio, Text, TextInput, Tooltip } from "@dehub/theme";
import type { UseFormReturnType } from "@dehub/theme";
import { BuildingIcon, MagnifyingGlassIcon, UserIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PubXypApi } from "@/apis";
import type { IReference } from "@/models/Reference";
import { humanizeError } from "@/utils/humanize-error";
import { message } from "@/utils/message";
import type { IFormData } from "./register-form";
import classes from "./register-form.module.css";

type Props = {
  form: UseFormReturnType<IFormData>;
  partnerTypes: IReference[];
};

const COMPANY_REGEX = /^\d{7}$/;
const CITIZEN_REGEX = /^[А-Яа-яҮүӨөЁё]{2}\d{8}$/;

export function RegisterFormIdentitySection({ form, partnerTypes }: Props) {
  const { t } = useTranslation("register");
  const isCitizen = form.values.type === "CITIZEN";
  const SectionIcon = isCitizen ? UserIcon : BuildingIcon;
  const sectionTitle = isCitizen ? t("form.citizenInfo") : t("form.orgInfo");
  const regNumberLabel = isCitizen ? t("form.regNumber") : t("form.taxNumber");
  const nameLabel = isCitizen ? t("form.lastName") : t("form.orgName");

  const [lookupLoading, setLookupLoading] = useState(false);

  const regNumber = form.values.regNumber ?? "";
  const regNumberFormatValid = isCitizen ? CITIZEN_REGEX.test(regNumber) : COMPANY_REGEX.test(regNumber);
  const lookupDisabled = !form.values.type || !regNumber || !regNumberFormatValid || lookupLoading;

  const handleLookup = async () => {
    if (!regNumberFormatValid) {
      message.error(isCitizen ? t("form.citizenRegFormatError") : t("form.companyRegFormatError"));
      return;
    }
    setLookupLoading(true);
    try {
      if (isCitizen) {
        const res = await PubXypApi.lookupCitizen(regNumber);
        if (!res.found) {
          message.error(t("form.citizenLookupNotFound"));
        } else {
          form.setValues((v) => ({ ...v, businessName: res.lastname, firstName: res.firstname }));
        }
      } else {
        const res = await PubXypApi.lookupLegalEntity(regNumber);
        if (!res.found) {
          message.error(t("form.companyLookupNotFound"));
        } else {
          // res.nameEn is the state registry's own Latin name. It is often empty,
          // and there is no fallback by design -- a transliterated company name
          // would be wrong (see localizeBusinessName in @dehub/theme).
          form.setValues((v) => ({ ...v, businessName: res.name, businessNameEng: res.nameEn || v.businessNameEng }));
        }
      }
    } catch (error: any) {
      // The lookup is a convenience, not a gate — whatever went wrong, the
      // applicant can still type the name in and finish registering. Say so,
      // rather than leaving them staring at a failure with no way forward.
      message.error(humanizeError(error?.message, error?.code));
    }
    setLookupLoading(false);
  };

  return (
    <Grid.Col span={12}>
      <Box className={classes.sectionHeader}>
        <Box className={classes.sectionIcon}>
          <SectionIcon size={18} />
        </Box>
        <Text className={classes.sectionTitle}>{sectionTitle}</Text>
      </Box>
      <Grid gap="md">
        <Grid.Col span={12}>
          <Radio.Group size="md" label={t("form.partnerType")} classNames={{ label: classes.label }} {...form.getInputProps("type")}>
            <Group mt="sm">
              {(partnerTypes ?? []).map((item) => (
                <Radio key={item.code} value={item.code} label={item.name} />
              ))}
            </Group>
          </Radio.Group>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            inputMode={isCitizen ? "text" : "numeric"}
            maxLength={isCitizen ? 10 : 7}
            disabled={!form.values.type}
            classNames={{
              root: classes.root,
              input: classes.input,
              label: classes.label,
            }}
            label={regNumberLabel}
            placeholder={regNumberLabel}
            withAsterisk
            rightSectionPointerEvents="all"
            rightSection={
              <Tooltip label={t("form.xypCheck")} withArrow>
                <ActionIcon
                  variant="subtle"
                  color="brand"
                  size="sm"
                  disabled={lookupDisabled}
                  onClick={handleLookup}
                  aria-label={t("form.xypCheck")}
                >
                  {lookupLoading ? <Loader size="xs" /> : <MagnifyingGlassIcon size={16} />}
                </ActionIcon>
              </Tooltip>
            }
            {...form.getInputProps("regNumber")}
            onChange={(e) =>
              form.setFieldValue(
                "regNumber",
                isCitizen
                  ? e.currentTarget.value.replace(/[^А-Яа-яҮүӨөЁё0-9]/g, "").slice(0, 10)
                  : e.currentTarget.value.replace(/\D/g, "").slice(0, 7),
              )
            }
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            classNames={{
              root: classes.root,
              input: classes.input,
              label: classes.label,
            }}
            placeholder={nameLabel}
            label={nameLabel}
            withAsterisk
            {...form.getInputProps("businessName")}
          />
        </Grid.Col>
        {!isCitizen && (
          <Grid.Col span={12}>
            <TextInput
              classNames={{
                root: classes.root,
                input: classes.input,
                label: classes.label,
                description: classes.description,
              }}
              placeholder="AIRWINGS BUREAU LLC"
              label={t("form.orgNameLatin")}
              description={t("form.orgNameLatinDesc")}
              inputWrapperOrder={["label", "input", "description", "error"]}
              {...form.getInputProps("businessNameEng")}
            />
          </Grid.Col>
        )}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            classNames={{
              root: classes.root,
              input: classes.input,
              label: classes.label,
            }}
            placeholder={t("form.myName")}
            label={t("form.myName")}
            withAsterisk
            {...form.getInputProps("firstName")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            classNames={{
              root: classes.root,
              input: classes.input,
              label: classes.label,
            }}
            inputMode="numeric"
            maxLength={8}
            label={t("form.phone")}
            placeholder={t("form.phone")}
            withAsterisk
            {...form.getInputProps("phone")}
            onChange={(e) => form.setFieldValue("phone", e.currentTarget.value.replace(/\D/g, "").slice(0, 8))}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            classNames={{
              root: classes.root,
              input: classes.input,
              label: classes.label,
            }}
            label={t("form.email")}
            placeholder={t("form.email")}
            withAsterisk
            {...form.getInputProps("email")}
          />
        </Grid.Col>
      </Grid>
    </Grid.Col>
  );
}
